import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  useTheme,
  Alert,
  Snackbar,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SchoolIcon from '@mui/icons-material/School';
import StarIcon from '@mui/icons-material/Star';
import { ref, push } from 'firebase/database';
import { database } from '../firebase';

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  phone: Yup.string()
    .matches(/^[0-9]{10}$/, 'Phone number must be 10 digits')
    .required('Phone number is required'),
  city: Yup.string().required('City is required'),
  class: Yup.string().required('Please select a class')
});

const DemoClass = () => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const theme = useTheme();

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      phone: '',
      city: '',
      class: ''
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const demoClassRef = ref(database, 'demoClasses');
        await push(demoClassRef, {
          ...values,
          timestamp: new Date().toISOString(),
          status: 'pending',
        });
        
        setSnackbarMessage('Demo class booked successfully! We will contact you shortly.');
        setSnackbarSeverity('success');
        formik.resetForm();
      } catch (error) {
        setSnackbarMessage('Error booking demo class. Please try again.');
        setSnackbarSeverity('error');
      }
      setOpenSnackbar(true);
    },
  });

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Typography variant="h3" component="h1" gutterBottom>
            Book a Free Demo Class
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" paragraph>
            Experience our Regular and Specialized Olympiad preparation methodology with a free demo class
          </Typography>

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Why Choose Our Regular and Olympiad Classes?
            </Typography>
            <Grid container spacing={2}>
              {[
                'Specialized training for IMO, ISO, IEO, ISSO and IHO',
                'Experienced Olympiad trainers',
                'Small batch sizes for personalized attention',
                'Flexible timings for working parents',
                'Comprehensive study materials',
                'Regular mock tests and assessments',
                'Focus on problem-solving skills',
                'Track record of producing medalists',
              ].map((benefit, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <StarIcon sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="body2">{benefit}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="name"
                      name="name"
                      label="Full Name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={formik.touched.name && formik.errors.name}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="email"
                      name="email"
                      label="Email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                      helperText={formik.touched.email && formik.errors.email}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="phone"
                      name="phone"
                      label="Phone Number"
                      value={formik.values.phone}
                      onChange={formik.handleChange}
                      error={formik.touched.phone && Boolean(formik.errors.phone)}
                      helperText={formik.touched.phone && formik.errors.phone}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="city"
                      name="city"
                      label="Current City"
                      value={formik.values.city}
                      onChange={formik.handleChange}
                      error={formik.touched.city && Boolean(formik.errors.city)}
                      helperText={formik.touched.city && formik.errors.city}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="class-label">Select Class</InputLabel>
                      <Select
                        labelId="class-label"
                        id="class"
                        name="class"
                        value={formik.values.class}
                        onChange={formik.handleChange}
                        error={formik.touched.class && Boolean(formik.errors.class)}
                      >
                        {Array.from({ length: 8 }, (_, i) => i + 3).map((cls) => (
                          <MenuItem key={cls} value={cls}>
                            Class {cls}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.class && formik.errors.class && (
                        <Typography color="error" variant="caption">
                          {formik.errors.class}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      disabled={formik.isSubmitting}
                    >
                      Book Demo Class
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DemoClass; 